import { Alert, Button, Modal } from '@payaca/ambrosia';
import { FC, useCallback, useEffect, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import SendJob from '../sendJob/SendJob';

import * as customerActions from '@payaca/store/customer/customerActions';
import * as dealsActions from '@payaca/store/deals/dealsActions';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as jobPaymentsActions from '@payaca/store/jobPayments/jobPaymentsActions';
import * as jobsStoreActions from '@payaca/store/jobs/jobsActions';

import { useEmailSuppressionResult } from '@/api/queries/emails/useGetEmailSuppressionResults';
import { useSelector } from '@/api/state';
import { getJobContentByJobId } from '@/utils/stateAccessors';

import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import { JobPayment } from '@payaca/types/jobPaymentTypes';
import './ResendJobModal.css';

type Props = {
  isOpen: boolean;
  jobId: number;
  onClose: () => void;
};
const ResendJobModal: FC<Props> = ({
  isOpen,
  jobId,
  onClose,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const isResendingJob = useSelector((state) => {
    return state.jobsStore.isResendingJob;
  });

  const resendJobErrorMessage = useSelector((state) => {
    return state.jobsStore.resendJobErrorMessage;
  });
  const [emailSuppressionErrorMessage, setEmailSuppressionErrorMessage] =
    useState<string>();

  const isResentJobSuccessfully = useSelector((state) => {
    return state.jobsStore.isResentJobSuccessfully;
  });

  const [resendEmailState, setResendEmailState] = useState<{
    preButtonEmailText: string;
    postButtonEmailText: string;
    sendMeACopy: boolean;
    isSendJobDisabled: boolean;
  }>({
    preButtonEmailText: '',
    postButtonEmailText: '',
    sendMeACopy: true,
    isSendJobDisabled: true,
  });

  useEffect(() => {
    // resent successfully and
    if (!isResendingJob && isResentJobSuccessfully) {
      dispatch(jobsStoreActions.clearResendJob());
      onClose();
    }
  }, [dispatch, isResendingJob, isResentJobSuccessfully, onClose]);

  useEffect(() => {
    dispatch(jobsStoreActions.requestGetJob(jobId));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [jobId]);

  const job = useSelector((state) => {
    return state.jobsStore.jobs && state.jobsStore.jobs[jobId]?.entity;
  });

  useEffect(() => {
    if (!job?.jobContentId) return;
    dispatch(jobContentActions.requestGetJobContent(job.jobContentId));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [job]);

  const jobContent = useSelector((state) => {
    return getJobContentByJobId(state, jobId);
  });

  const deal = useSelector((state) => {
    if (!job?.dealId) return;
    return state.deals.deals && state.deals.deals[job.dealId]?.entity;
  });

  useEffect(() => {
    if (job) {
      dispatch(jobPaymentsActions.requestGetJobPaymentsForDeal(job?.dealId));
      dispatch(dealsActions.requestGetDeal(job.dealId));
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'job', and 'dispatch'
  }, [job?.dealId]);

  useEffect(() => {
    if (deal?.customerId) {
      dispatch(
        customerActions.requestGetAndSetCurrentCustomer(deal.customerId)
      );
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [deal?.customerId]);

  const jobPayments: JobPayment[] = useSelector((state) => {
    if (!job?.dealId) return [];

    const deal = state.deals.deals && state.deals.deals[job?.dealId]?.entity;
    const jobPaymentIds = deal?.jobPaymentIds || [];

    const payments = jobPaymentIds.map((jobPaymentId: number) => {
      return state.jobPayments.jobPayments[jobPaymentId]?.entity;
    });

    return payments.filter((x?: JobPayment) => !!x);
  });

  const customer = useSelector((state) => state.customer.currentCustomer);

  const customerContactEmail =
    customer && job
      ? getJobContactFromCustomer(customer, job.contactId)?.emailAddress
      : undefined;

  const emailSuppressionResult =
    useEmailSuppressionResult(customerContactEmail);

  const onResendJob = useCallback(() => {
    setEmailSuppressionErrorMessage(undefined);
    if (emailSuppressionResult?.isSuppressed) {
      setEmailSuppressionErrorMessage(
        `The Customer's email is blocked. Reason: ${emailSuppressionResult.reason}`
      );
      return;
    }

    dispatch(
      jobsStoreActions.requestResendJob(jobId, {
        preButtonEmailText: resendEmailState.preButtonEmailText,
        postButtonEmailText: resendEmailState.postButtonEmailText,
        sendMeACopy: resendEmailState.sendMeACopy,
      })
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: onClose
  }, [dispatch, jobId, onClose, resendEmailState, emailSuppressionResult]);

  return (
    <Modal
      isOpen={isOpen}
      title="Resend job"
      size="md"
      onClose={onClose}
      className="resend-job-modal"
    >
      <Modal.Body>
        {job && jobContent && (
          <SendJob
            job={job}
            jobContent={jobContent}
            jobPayments={jobPayments}
            customer={customer! /* FIXME */}
            preButtonEmailText={resendEmailState.preButtonEmailText || ''}
            postButtonEmailText={resendEmailState.postButtonEmailText || ''}
            sendMeACopy={resendEmailState.sendMeACopy}
            onUpdate={(change: any) => {
              setResendEmailState((resendEmailState) => ({
                ...resendEmailState,
                ...change,
              }));
            }}
            onSetIsSendJobDisabled={(isSendJobDisabled: boolean) => {
              setResendEmailState((resendEmailState) => {
                return {
                  ...resendEmailState,
                  isSendJobDisabled: isSendJobDisabled,
                };
              });
            }}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {emailSuppressionErrorMessage && (
          <Alert colour="red" className="mb-4">
            <strong>This Proposal could not be sent:</strong>
            <ul>
              <li>{emailSuppressionErrorMessage}</li>
            </ul>
          </Alert>
        )}
        {resendJobErrorMessage && (
          <Alert colour="red" className="mb-4">
            Sorry, there was an error resending your job. Please try again.
          </Alert>
        )}
        <Modal.Footer.Actions>
          <Button
            onClick={() => !isResendingJob && onResendJob()}
            disabled={resendEmailState.isSendJobDisabled}
            isProcessing={isResendingJob}
          >
            Send
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default ResendJobModal;
